import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { putMyName, getMyPhone, putMyPhone, formatPhone } from "@/lib/applyApi";
import { AppHeader } from "@/components/brand/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ContactPrefsCard, ContractsPlaceholder } from "@/components/SellerPortal";
import { NewsletterCard } from "@/components/seller/NewsletterCard";

// Edits the existing account name (users.name).
function NameField() {
  const { user, setUser } = useAuth();
  const [v, setV] = useState(user?.name || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setV(user?.name || ""); }, [user?.name]);
  const save = async () => {
    setBusy(true);
    try {
      const r = await putMyName(v);
      setUser((u) => ({ ...u, name: r.name }));
      toast.success("Name saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
      <Input data-testid="settings-name-input" value={v} onChange={(e) => setV(e.target.value)} placeholder="Your full name" autoComplete="name" className="h-10 sm:max-w-xs" />
      <Button data-testid="settings-name-save" onClick={save} disabled={busy || v.trim() === (user?.name || "") || v.trim().length < 2}
        className="h-10 rounded-full bg-amber-800 hover:bg-amber-900 text-white">Save name</Button>
    </div>
  );
}

// Phone saved on the existing account contact profile.
function PhoneField() {
  const [saved, setSaved] = useState(null);
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { getMyPhone().then((r) => { setSaved(r.phone || ""); setV(r.phone || ""); }).catch(() => setSaved("")); }, []);
  const save = async () => {
    setBusy(true);
    try { const r = await putMyPhone(v); setSaved(r.phone); setV(r.phone); toast.success("Phone saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  if (saved === null) return null;
  return (
    <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
      <Input data-testid="settings-phone-input" value={v} onChange={(e) => setV(formatPhone(e.target.value))} placeholder="Your phone number" inputMode="tel" autoComplete="tel" className="h-10 sm:max-w-xs" />
      <Button data-testid="settings-phone-save" onClick={save} disabled={busy || !v.trim() || v === saved}
        className="h-10 rounded-full bg-amber-800 hover:bg-amber-900 text-white">Save phone</Button>
    </div>
  );
}

// Seller Portal Account Settings: reuses the existing account email, contact preference/consent and trainer controls.
export default function SellerSettings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (id) setTimeout(() => document.getElementById(id)?.scrollIntoView({ block: "start", behavior: "smooth" }), 400);
  }, []);
  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <AppHeader title="Account Settings" home="/dashboard" width="max-w-3xl" onBack={() => navigate("/dashboard")} backTestId="settings-back" backLabel="Back to Dashboard" />
      <main data-testid="seller-settings" className="max-w-3xl mx-auto px-4 sm:px-8 py-6 sm:py-8">
        <section id="settings-personal" data-testid="settings-personal" className="bg-white border border-stone-200 rounded-xl p-4 mb-4 scroll-mt-20">
          <h2 className="font-semibold text-stone-900 flex items-center gap-2 mb-2"><UserRound className="h-4 w-4 text-amber-800" /> Personal Information</h2>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm items-center">
            <dt className="text-stone-500">Name</dt><dd data-testid="settings-name" className="font-medium text-stone-900"><NameField /></dd>
            <dt className="text-stone-500">Email</dt><dd data-testid="settings-email" className="font-medium text-stone-900 break-all">{user?.email}</dd>
            <dt className="text-stone-500">Phone</dt><dd data-testid="settings-phone" className="font-medium text-stone-900"><PhoneField /></dd>
          </dl>
        </section>
        <ContactPrefsCard />
        <NewsletterCard />
        <ContractsPlaceholder />
      </main>
    </div>
  );
}
